import { getValue } from '../../helpers/utils';
import { evaluateObject } from '../../helpers/evaluators';
import { isSSR } from '@front10/utils';

const getPageContext = () => {
  if (window && window.EM && window.EM.context) {
    return window.EM.context;
  }
  return false;
};

const isValidContext = (context) => {
  const airlineIataCode = getValue(context, 'airline.code');
  const language = getValue(context, 'geo.language.lang');
  const siteEdition = getValue(context, 'geo.language.site_edition');
  const pageType = getValue(context, 'datasource.step');
  const deviceCategory = getValue(context, 'audience.device_category') || 'desktop';
  if (airlineIataCode && language && siteEdition && pageType && deviceCategory) {
    return true;
  }
  return false;
};

/**
 *
 * @param {context is from everymundo pageContext} context
 * This function return the most common expression used in everymundo pages
 */
const getCoolContext = (context) => {
  if (isValidContext(context)) {
    let market = getValue(context, 'geo.language.siteEditionMarket');
    if (market === undefined || market === null) {
      market = [];
    } else if (typeof market === 'string') {
      market = [market];
    }
    let countryGeoId = getValue(context, 'geo.language.site_edition_country_geo_id');
    if (countryGeoId === undefined || countryGeoId === null) {
      countryGeoId = [];
    } else if (typeof countryGeoId === 'string') {
      countryGeoId = [countryGeoId];
    }

    let travelClasses = getValue(context, 'dynamicContext.productType');
    if (typeof travelClasses === 'string') {
      travelClasses = [travelClasses];
    }
    return {
      airlineIataCode: getValue(context, 'airline.code'),
      provider: getValue(context, 'airline.name'),
      market: market,
      language: getValue(context, 'geo.language.lang'),
      textDirection: getValue(context, 'geo.language.textDirection'),
      siteEdition: getValue(context, 'geo.language.site_edition'),
      countryGeoId: countryGeoId,
      pageType: getValue(context, 'datasource.step'),
      renderType: isSSR() ? 'SSR' : 'CSR',
      url: getValue(context, 'datasource.url'),
      deviceCategory: getValue(context, 'audience.device_category') || 'desktop',
      originAirportsIataCode:
        getValue(context, 'dynamicContext.originLocations') ||
        getValue(context, 'extra_info.airports.origin') ||
        [],
      destinationAirportsIataCode:
        getValue(context, 'dynamicContext.destinationLocations') ||
        getValue(context, 'extra_info.airports.destination') ||
        [],
      userInputOriginAirportIataCode:
        getValue(context, 'dynamicContext.originLocation') ||
        getValue(context, 'geo.location.user_input_origin_airport_code'),
      userInputDestinationAirportIataCode:
        getValue(context, 'dynamicContext.destinationLocation') ||
        getValue(context, 'geo.location.user_input_destination_airport_code'),
      geoOriginId: getValue(context, 'geo.location.origin_geo_id'),
      geoDestinationId: getValue(context, 'geo.location.destination_geo_id'),
      userInputOriginCityName: getValue(context, 'geo.location.user_input_origin_city_name'),
      userInputDestinationCityName: getValue(
        context,
        'geo.location.user_input_destination_city_name'
      ),
      userInputOriginStateName: getValue(context, 'geo.location.user_input_origin_state_name'),
      userInputDestinationStateName: getValue(
        context,
        'geo.location.user_input_destination_state_name'
      ),
      budgetMaximum: getValue(context, 'dynamicContext.budgetMaximumAmount'),
      redemptionUnit: getValue(context, 'dynamicContext.budgetRedemptionUnit'),
      passengerCount: getValue(context, 'dynamicContext.passengerCount'),
      flightType: getValue(context, 'dynamicContext.productCategory'),
      travelClasses,
      departureDate: getValue(context, 'dynamicContext.departureDate') || 0,
      returnDate: getValue(context, 'dynamicContext.returnDate') || 90,
      flexibleDates: getValue(context, 'dynamicContext.isFlexibleDates'),
      discountCode: getValue(context, 'dynamicContext.discountCode')
    };
  }
  return false;
};

/**
 *
 * @param {context is from everymundo pageContext} context
 * This function return the most common expression used in everymundo pages
 */
const getPropertyContext = (context) => {
  if (isValidContext(context)) {
    let market = getValue(context, 'geo.language.siteEditionMarket');
    if (typeof market === 'string') {
      market = [market];
    }
    return {
      airlineIataCode: getValue(context, 'airline.code'),
      provider: getValue(context, 'airline.name'),
      market: market,
      language: getValue(context, 'geo.language.lang'),
      textDirection: getValue(context, 'geo.language.textDirection'),
      siteEdition: getValue(context, 'geo.language.site_edition'),
      pageType: getValue(context, 'datasource.step'),
      renderType: isSSR() ? 'SSR' : 'CSR',
      url: getValue(context, 'datasource.url'),
      deviceCategory: getValue(context, 'audience.device_category') || 'desktop',
      propertiesCodes: getValue(context, 'extra_info.properties.codes') || [],
      levelDestination: getValue(context, 'geo.location.level_destination'),
      geoDestinationId: getValue(context, 'geo.location.destination_geo_id'),
      userInputPropertyCode: getValue(context, 'geo.location.user_input_property_code'),
      userInputPropertyName: getValue(context, 'geo.location.user_input_property_name'),
      userInputPropertyCityName: getValue(context, 'geo.location.user_input_property_city_name'),
      userInputPropertyRegionName: getValue(
        context,
        'geo.location.user_input_property_region_name'
      ),
      userInputPropertyCountryName: getValue(
        context,
        'geo.location.user_input_property_country_name'
      ),
      userInputPropertyCountryCode: getValue(
        context,
        'geo.location.user_input_property_country_code'
      ),

      userInputPropertyStateName: getValue(context, 'geo.location.user_input_property_state_name')
    };
  }
  return false;
};

const transformCurrencySettingsToCurrencyPriceSettings = (currencySettings, priceFormat) => {
  return Object.keys(currencySettings).map((i) => {
    const currency = currencySettings[i];
    if (!currency.decimalPlaces) {
      currency.decimalPlaces = priceFormat.decimalPlaces;
    }
    return { currencyCode: i, priceFormat: currency };
  });
};

/**
 * This function return the prePopulation object after evaluate
 * all evaluators expression.
 */
const getPrepopulation = (cmpConfig) => {
  const {
    containerConfig: { settings } // settings
  } = cmpConfig;

  // Component prePopulation
  const prePopulationCopy = { ...settings.prePopulation };
  const evaluatedPrepopulation = evaluateObject(prePopulationCopy, cmpConfig);
  if (
    evaluatedPrepopulation.currencySettings !== '' &&
    typeof evaluatedPrepopulation.currencySettings === 'object' &&
    evaluatedPrepopulation.currencySettings.constructor === Object &&
    evaluatedPrepopulation.priceFormat !== '' &&
    typeof evaluatedPrepopulation.priceFormat === 'object' &&
    evaluatedPrepopulation.priceFormat.constructor === Object
  ) {
    evaluatedPrepopulation.currencyPricingSettings = transformCurrencySettingsToCurrencyPriceSettings(
      evaluatedPrepopulation.currencySettings,
      evaluatedPrepopulation.priceFormat
    );
    evaluatedPrepopulation.outputCurrencies = evaluatedPrepopulation.currencyPricingSettings.map(
      (i) => i.currencyCode
    );
  }
  delete evaluatedPrepopulation.currencySettings;
  if (evaluatedPrepopulation) {
    return evaluatedPrepopulation;
  }
  return prePopulationCopy;
};

export { getPageContext, isValidContext, getCoolContext, getPropertyContext, getPrepopulation };
